import { DOCUMENT } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import { Component, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { CountdownService } from '@core-mkt/services/countdown/countdown.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { PrivacyCheckService } from '@core-mkt/services/privacy-check/privacy-check.service';
import { ScriptLoaderService } from '@core-mkt/services/script-loader/script-loader.service';
import { EmailCaptureComponent } from '@core-mkt/shared/components/email-capture/email-capture/email-capture.component';
import { PrivacyManagerComponent } from '@core-mkt/shared/components/privacy-manager/privacy-manager/privacy-manager.component';
import { cloneDeep } from 'lodash-es';
import { FooterLinkComponent } from '../footer-link/footer-link.component';
import type { EmailCapture, Footer, FooterLinkGroup, FooterLogo, ShareButtons } from './footer';
import { footerData } from './footer-data';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';

import { Router } from '@angular/router';
import type { Subscription } from 'rxjs';
import { Subject } from 'rxjs';

@Component({
  selector: 'footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  standalone: true,
  imports: [FooterLinkComponent, EmailCaptureComponent, PrivacyManagerComponent, QueryParamDirective],
})
export class FooterComponent implements AfterViewInit, OnDestroy {
  pageData: Footer;
  resolverData: PageDataResponse;
  showStickyEmailCapture = true;
  displayRE = true;
  private readonly destroy$ = new Subject<void>();
  promoBannerActive = true;
  offerEndTime: number = null;
  offerStartTime: number = null;
  graphicBanner: HTMLElement;
  showPrivacyManagerModal = false;
  private readonly copyrightYear_ = new Date().getFullYear();
  routerEventSubscription: Subscription | undefined;

  constructor(
    private route: ActivatedRoute,
    private countdownService: CountdownService,
    private accessibilityService: AccessibilityService,
    public scriptLoaderService: ScriptLoaderService,
    public privacyCheckService: PrivacyCheckService,
    private env: EnvService,
    @Inject(DOCUMENT) private document: Document,
    private _router: Router,
  ) {
    this.pageData = footerData;

    //Getting aria label to ShareButtons
    this.pageData.aceShareGroup.forEach((button) => {
      button.ariaLabel = this.accessibilityService.getAriaLabel(button.link);
    });

    if (this.route.snapshot.data.pageData) {
      this.resolverData = cloneDeep(this.route.snapshot.data.pageData);
      if (this.resolverData.craftData.header) {
        let aaGraphicPromoBanner;
        let realEstateHeaderPromoBanner;
        if (this.resolverData.craftData.header?.aaGraphicPromoBanner) {
          aaGraphicPromoBanner = this.resolverData.craftData.header?.aaGraphicPromoBanner[0];
        }
        if (this.resolverData.craftData.header?.realEstateHeaderPromoBanner) {
          realEstateHeaderPromoBanner = this.resolverData.craftData.header?.realEstateHeaderPromoBanner[0];
          this.promoBannerActive = realEstateHeaderPromoBanner.showHidePromoBanner;
        }
        if (
          aaGraphicPromoBanner &&
          aaGraphicPromoBanner.showHideGraphicPromoBanner &&
          realEstateHeaderPromoBanner &&
          realEstateHeaderPromoBanner.showHidePromoBanner
        ) {
          const startTime = aaGraphicPromoBanner.startTime;
          const endTime = aaGraphicPromoBanner.endTime;

          if (startTime == null && endTime == null) {
            this.displayRE = true;
          } else if (startTime && endTime) {
            this.displayRE = this.countdownService.displayBanner(startTime, endTime);
          }
        }
      }
      if (this.resolverData.craftData.banners && this.resolverData.craftData.banners[0]) {
        const promoData = this.resolverData.craftData.banners[0].topBanner[0];
        if (promoData.showHidePromoBanner) {
          const startTime = promoData.promotionStartDateTime;
          const endTime = promoData.promotionEndsDateTime;

          if (startTime == null && endTime == null) {
            this.displayRE = true;
          } else if (startTime && endTime) {
            const startTimeStamp = new Date(startTime).getTime();
            const endTimeStamp = new Date(endTime).getTime();
            this.displayRE = this.countdownService.displayBanner(startTimeStamp, endTimeStamp);
          }
        }
      }
    } else {
      this.pageData?.emailCapture[0];
    }
  }

  ngAfterViewInit(): void {
    this.graphicBanner = this.document.getElementById('graphicBanner');
  }

  get copyrightYear(): number {
    return this.copyrightYear_;
  }

  get showHideFooter(): boolean {
    return !this.resolverData?.craftData?.headerData?.hideFooter;
  }

  get footerLogos(): FooterLogo[] {
    return this.pageData.aceFooterLogo;
  }

  get generalLinks(): FooterLinkGroup[] {
    return this.pageData.aceGeneralLinkGroup;
  }

  get socialLinks(): FooterLinkGroup[] {
    return this.pageData.aceSocialLinkGroup;
  }

  get verticalLinks(): FooterLinkGroup[] {
    return this.pageData.aceVerticalLinkGroup;
  }

  get bottomLinks(): FooterLinkGroup[] {
    return this.pageData.bottomLinkGroup;
  }

  get emailCaptureData(): EmailCapture {
    return this.pageData.emailCapture[0];
  }

  get shareButtons(): ShareButtons[] {
    return this.pageData.aceShareGroup;
  }

  itemLabel(button: ShareButtons): string {
    return button.label;
  }

  stripLink(rawLink: string): string {
    return rawLink.replace(/\w+:\/\//, '');
  }

  openPrivacyManagerModal() {
    this.showPrivacyManagerModal = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
